
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { GroupMember, GroupRole } from "@/lib/types";
import { toast } from "sonner";
import { safeSelect } from "@/utils/supabase/queries";

export function useMembersList(groupId: string) {
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchMembers();

    // Set up a realtime subscription for member changes
    const channel = supabase
      .channel("group-members-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "group_members",
          filter: `group_id=eq.${groupId}`,
        },
        () => {
          fetchMembers();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [groupId]);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      
      // Get all members for this group using safe select
      const membersData = await safeSelect(
        supabase
          .from("group_members")
          .select("*")
          .eq("group_id", groupId as any),
        "Failed to load members"
      );

      if (!membersData || membersData.length === 0) {
        setMembers([]);
        setLoading(false);
        return;
      }

      // Extract member user IDs with proper type checking
      const memberIds = membersData
        .map((member: any) => member?.user_id)
        .filter((id: any): id is string => Boolean(id));
      
      if (memberIds.length === 0) {
        setMembers([]);
        setLoading(false);
        return;
      }
      
      // Fetch user details using public_profiles view for security
      const usersData = await safeSelect(
        supabase
          .from("public_profiles")
          .select("id, username, avatar_id, bio")
          .in("id", memberIds as any),
        "Failed to load user profiles"
      );

      // Create a map of users by ID
      const usersMap: Record<string, {
        id: string;
        username: string;
        avatarId: number | null;
        bio: string | null;
        isSubscriber: boolean;
        isDonator: boolean;
      }> = {};
      
      if (usersData) {
        usersData.forEach((user: any) => {
          if (user?.id) {
            usersMap[user.id] = {
              id: user.id,
              username: user.username || '',
              avatarId: user.avatar_id || null,
              bio: user.bio || "",
              isSubscriber: false,
              isDonator: false,
            };
          }
        });
      }

      // Combine member data with user data
      const formattedMembers = membersData
        .filter((member: any) => {
          return member?.id && 
                 member?.user_id &&
                 usersMap[member.user_id];
        })
        .map((member: any) => {
          return {
            id: member.id,
            groupId: member.group_id,
            userId: member.user_id,
            role: member.role as GroupRole,
            isBanned: member.is_banned || false,
            isMuted: member.is_muted || false,
            mutedUntil: member.muted_until,
            joinedAt: member.joined_at,
            user: usersMap[member.user_id],
          };
        });

      // Sort members by role
      formattedMembers.sort((a, b) => {
        const roleOrder = { admin: 0, moderator: 1, member: 2 };
        return roleOrder[a.role] - roleOrder[b.role];
      });

      setMembers(formattedMembers);
    } catch (error) {
      console.error("Error fetching members:", error);
      toast.error("Failed to load members. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const filteredMembers = members.filter(member => {
    if (!searchTerm) return true;
    return member.user?.username.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return {
    members,
    filteredMembers,
    loading,
    searchTerm, 
    setSearchTerm,
    fetchMembers
  };
}
